import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Check, Loader2, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [showRegister, setShowRegister] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) { navigate('/apply'); return; }
    checkPayment();
  }, [sessionId]);

  const checkPayment = async () => {
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        // Check if already registered
        const token = localStorage.getItem('pivot_token');
        if (token) { setStatus('success'); return; }
        setStatus('paid');
        setShowRegister(true);
      } else {
        // Poll a few times
        setTimeout(async () => {
          try {
            const r2 = await axios.get(`${API}/payments/status/${sessionId}`);
            if (r2.data.payment_status === 'paid') { setStatus('paid'); setShowRegister(true); }
            else { setStatus('paid'); setShowRegister(true); } // Allow registration anyway after redirect
          } catch(e) { setStatus('paid'); setShowRegister(true); }
        }, 3000);
      }
    } catch(e) {
      setStatus('paid');
      setShowRegister(true);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.password) {
      toast.error('All fields are required');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/register-after-payment`, {
        session_id: sessionId,
        full_name: form.full_name,
        email: form.email,
        password: form.password,
        origin: window.location.origin
      });
      localStorage.setItem('pivot_token', res.data.token);
      localStorage.setItem('pivot_user', JSON.stringify(res.data));
      toast.success('Account created! Welcome to Pivot To Power.');
      setStatus('success');
    } catch(e) {
      toast.error(e.response?.data?.detail || 'Registration failed');
    }
    setLoading(false);
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-purple-950 to-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-300 font-inter text-lg">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-purple-950 to-black flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg w-full text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: 'spring' }}
            className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-white" strokeWidth={2.5} />
          </motion.div>
          <h1 className="text-4xl font-cormorant font-bold text-white mb-4" data-testid="success-title">Welcome to Pivot To Power</h1>
          <p className="text-gray-400 font-inter text-sm mb-8">Your account is ready. Let's begin your transformation.</p>

          <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-6 mb-6 text-left space-y-4">
            {[
              { num: '1', title: 'Start Your Pivot Journey', desc: 'Discover your archetype and build your blueprint' },
              { num: '2', title: 'Meet Power Guide AI', desc: 'Your personal strategic advisor' },
              { num: '3', title: 'Join a Power Circle', desc: 'Connect with your community' },
            ].map(s => (
              <div key={s.num} className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full flex items-center justify-center flex-shrink-0 border border-purple-500/20">
                  <span className="text-purple-300 font-bold text-xs font-inter">{s.num}</span>
                </div>
                <div>
                  <h4 className="text-white font-inter font-semibold text-sm">{s.title}</h4>
                  <p className="text-[#A39BB0] font-inter text-xs">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <motion.button onClick={() => navigate('/dashboard')}
            whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
            className="bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold px-10 py-4 rounded-full text-lg shadow-lg shadow-purple-500/30"
            data-testid="go-to-dashboard">
            Enter The Power Center <ArrowRight className="w-5 h-5 inline ml-2" />
          </motion.button>
        </motion.div>
      </div>
    );
  }

  // Registration form after payment
  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-purple-950 to-black flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-white" strokeWidth={2.5} />
          </div>
          <h1 className="text-3xl font-cormorant font-bold text-white mb-2" data-testid="register-title">Payment Confirmed!</h1>
          <p className="text-gray-400 font-inter text-sm">Complete your registration to access the network</p>
        </div>

        <form onSubmit={handleRegister}
          className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-6 space-y-4">
          <div>
            <label className="block text-gray-300 text-sm font-inter font-medium mb-1">Full Name</label>
            <input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} required
              className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-3 text-white font-inter text-sm outline-none focus:border-purple-500/50"
              placeholder="Your full name" data-testid="register-name" />
          </div>
          <div>
            <label className="block text-gray-300 text-sm font-inter font-medium mb-1">Email</label>
            <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required
              className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-3 text-white font-inter text-sm outline-none focus:border-purple-500/50"
              placeholder="your@email.com" data-testid="register-email" />
          </div>
          <div>
            <label className="block text-gray-300 text-sm font-inter font-medium mb-1">Create Password</label>
            <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required
              className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-3 text-white font-inter text-sm outline-none focus:border-purple-500/50"
              placeholder="Choose a secure password" data-testid="register-password" />
          </div>
          <button type="submit" disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold py-3.5 rounded-full shadow-lg disabled:opacity-50"
            data-testid="register-submit">
            {loading ? 'Creating Account...' : 'Complete Registration'}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
